// ===================================================================
// SHELTER — Incident Reports
// Shelter incident logging with severity, resolution tracking
// ===================================================================

import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Textarea,
  Modal, Stack, SimpleGrid, Table, ActionIcon, Drawer, Divider,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconRefresh, IconCheck, IconX,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { INCIDENT_REPORT_TYPES } from '../../types/shelter';
import type { ShelterIncident, IncidentSeverity } from '../../types/shelter';
import { queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';
import { formatPhone } from '../../lib/phoneFormat';

const SEVERITY_COLORS: Record<IncidentSeverity, string> = {
  low: 'var(--color-primary-700)', medium: '#C68700', high: '#F86A0B', critical: 'var(--color-error)',
};

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const EMPTY: Partial<ShelterIncident> = {
  shelterId: '', reportType: 'other', severity: 'low',
  description: '', involvedPartyIds: '[]', respondingStaff: '',
  resolution: '', notes: '',
};

export default function Incidents() {
  const { activeIncident, user } = useAuthStore();
  const { canEdit } = useModulePermission('shelter');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    shelterIncidents, shelters,
    loadingIncidents,
    loadShelterIncidents, loadShelters,
    createShelterIncident, updateShelterIncident,
  } = useShelterStore();

  const [search, setSearch] = useState('');

  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const [shelterFilter, setShelterFilter] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string | null>(null);
  const [resolvedFilter, setResolvedFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ShelterIncident | null>(null);
  const [form, setForm] = useState<Partial<ShelterIncident>>({ ...EMPTY });
  const [involvedPeople, setInvolvedPeople] = useState<{id: string; name: string; phone: string}[]>([]);
  const [peopleSearch, setPeopleSearch] = useState('');
  const [peopleResults, setPeopleResults] = useState<{id: string; name: string; phone: string}[]>([]);
  const [peopleSearching, setPeopleSearching] = useState(false);
  const peopleSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saving, setSaving] = useState(false);
  const [detailTarget, setDetailTarget] = useState<ShelterIncident | null>(null);

  const handlePeopleSearch = useCallback((q: string) => {
    setPeopleSearch(q);
    if (peopleSearchRef.current) clearTimeout(peopleSearchRef.current);
    if (!q.trim()) { setPeopleResults([]); return; }
    peopleSearchRef.current = setTimeout(async () => {
      setPeopleSearching(true);
      try {
        const r = await queryRecords(TABLES.people, { search: q.trim(), limit: 8 });
        const fp = FIELDS.people;
        setPeopleResults(r.records.map((row: Record<string, unknown>) => ({
          id: String(row.record_id),
          name: `${row[fp.firstName] ?? ''} ${row[fp.lastName] ?? ''}`.trim(),
          phone: String(row[fp.phone] ?? ''),
        })));
      } catch { /* ignore */ }
      finally { setPeopleSearching(false); }
    }, 300);
  }, []);

  const addInvolvedPerson = (p: {id: string; name: string; phone: string}) => {
    if (!involvedPeople.find(x => x.id === p.id)) {
      setInvolvedPeople(prev => [...prev, p]);
    }
    setPeopleSearch('');
    setPeopleResults([]);
  };

  const removeInvolvedPerson = (id: string) => setInvolvedPeople(prev => prev.filter(p => p.id !== id));

  useEffect(() => {
    loadShelterIncidents(); loadShelters();
  }, [incidentId, loadShelterIncidents, loadShelters]);

  // Store already server-side filters by active incident; use directly
  const iIncidents = incidentId ? shelterIncidents : [];
  const iShelters = incidentId ? shelters : [];

  const shelterName = (id: string) => shelters.find(s => String(s.id) === id)?.name ?? id;
  const reportTypeLabel = (val: string) => INCIDENT_REPORT_TYPES.find(t => t.value === val)?.label ?? val;

  const shelterSelectData = useMemo(() => iShelters.map(s => ({ value: String(s.id), label: s.name })), [iShelters]);

  const filtered = useMemo(() => {
    let list = iIncidents;
    if (shelterFilter && shelterFilter !== 'all') list = list.filter(i => i.shelterId === shelterFilter);
    if (severityFilter && severityFilter !== 'all') list = list.filter(i => i.severity === severityFilter);
    if (resolvedFilter === 'resolved') list = list.filter(i => !!i.resolvedAt);
    if (resolvedFilter === 'unresolved') list = list.filter(i => !i.resolvedAt);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        i.description.toLowerCase().includes(q) ||
        i.reportType.toLowerCase().includes(q) ||
        i.respondingStaff.toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return (b.id || 0) - (a.id || 0);
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [iIncidents, shelterFilter, severityFilter, resolvedFilter, search, sortField, sortDir]);

  const setField = (key: keyof ShelterIncident, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY });
    setInvolvedPeople([]);
    setPeopleSearch('');
    setPeopleResults([]);
    setModalOpen(true);
  };

  const openEdit = (i: ShelterIncident) => {
    setEditTarget(i);
    setForm({ ...i });
    // Restore involved people from stored IDs — names stored as JSON of {id,name} pairs if available,
    // otherwise fall back to ID-only placeholders
    try {
      const parsed = JSON.parse(i.involvedPartyIds);
      if (Array.isArray(parsed) && parsed.length > 0) {
        if (typeof parsed[0] === 'object') {
          setInvolvedPeople(parsed);
        } else {
          // Legacy: plain string IDs — show as ID only
          setInvolvedPeople(parsed.map((id: string) => ({ id, name: id, phone: '' })));
        }
      } else {
        setInvolvedPeople([]);
      }
    } catch { setInvolvedPeople([]); }
    setPeopleSearch('');
    setPeopleResults([]);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.description?.trim()) {
      notifications.show({ message: 'Description is required', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        incidentId,
        involvedPartyIds: JSON.stringify(involvedPeople.map(p => p.id)),
        reportedAt: editTarget ? form.reportedAt : new Date().toISOString(),
        reportedBy: editTarget ? form.reportedBy : (user?.fullName ?? ''),
      };

      if (editTarget) {
        await updateShelterIncident(editTarget.id, payload);
        notifications.show({ message: 'Incident updated', color: 'var(--color-success)' });
      } else {
        await createShelterIncident(payload);
        notifications.show({ message: 'Incident reported', color: 'var(--color-success)' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleResolve = async (i: ShelterIncident) => {
    try {
      await updateShelterIncident(i.id, {
        resolvedAt: new Date().toISOString(),
        resolution: i.resolution || 'Resolved',
      });
      notifications.show({ message: 'Incident resolved', color: 'var(--color-success)' });
    } catch {
      notifications.show({ message: 'Failed to resolve', color: 'var(--color-error)' });
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Incident Reports</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingIncidents} onClick={() => loadShelterIncidents()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Report Incident</Button>}
        </Group>
      </Group>

      <Group gap="xs" mb="md">
        <TextInput size="xs" placeholder="Search..." leftSection={<IconSearch size={14} />}
          value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1 }} />
        <Select size="xs" placeholder="Shelter" data={[{ value: 'all', label: 'All Shelters' }, ...shelterSelectData]}
          value={shelterFilter} onChange={setShelterFilter} clearable w={180} />
        <Select size="xs" placeholder="Severity" data={[{ value: 'all', label: 'All Severities' }, ...SEVERITY_OPTIONS]}
          value={severityFilter} onChange={setSeverityFilter} clearable w={140} />
        <Select size="xs" placeholder="Status" data={[
          { value: 'all', label: 'All' },
          { value: 'resolved', label: 'Resolved' },
          { value: 'unresolved', label: 'Unresolved' },
        ]} value={resolvedFilter} onChange={setResolvedFilter} clearable w={140} />
      </Group>

      <Paper withBorder radius="md">
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 380px)' }}>
        <Table striped highlightOnHover>
          <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
            <Table.Tr>
              <Table.Th>Shelter</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('severity')}>Severity{sortIcon('severity')}</Table.Th>
              <Table.Th>Description</Table.Th>
              <Table.Th>Reported By</Table.Th>
              <Table.Th>Date</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</Table.Th>
              <Table.Th w={80}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 && (
              <Table.Tr><Table.Td colSpan={8}><Text c="dimmed" ta="center" py="lg">No incidents</Text></Table.Td></Table.Tr>
            )}
            {filtered.map(i => (
              <Table.Tr key={i.id} onClick={() => setDetailTarget(i)} style={{ cursor: 'pointer' }}>
                <Table.Td>{i.shelterId ? shelterName(i.shelterId) : '-'}</Table.Td>
                <Table.Td><Badge size="xs" variant="light">{reportTypeLabel(i.reportType)}</Badge></Table.Td>
                <Table.Td>
                  <Badge size="xs" color={SEVERITY_COLORS[i.severity]}>{i.severity}</Badge>
                </Table.Td>
                <Table.Td maw={250} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {i.description}
                </Table.Td>
                <Table.Td>{i.reportedBy || '-'}</Table.Td>
                <Table.Td>{i.reportedAt ? new Date(i.reportedAt).toLocaleDateString() : '-'}</Table.Td>
                <Table.Td>
                  {i.resolvedAt ? (
                    <Badge size="xs" color="var(--color-success)">Resolved</Badge>
                  ) : (
                    <Badge size="xs" color="var(--color-error)" variant="light" style={{ cursor: 'pointer' }}
                      onClick={() => handleResolve(i)}>Unresolved</Badge>
                  )}
                </Table.Td>
                {canEdit && (
                <Table.Td onClick={e => e.stopPropagation()}>
                  <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(i)}>
                    <IconPencil size={14} />
                  </ActionIcon>
                </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        </div>
      </Paper>

      <Text size="xs" c="dimmed" mt="xs">{filtered.length} incident{filtered.length !== 1 ? 's' : ''}</Text>

      {/* Detail Drawer */}
      <Drawer opened={!!detailTarget} onClose={() => setDetailTarget(null)} position="right" size="md"
        title={<Text fw={700} size="lg">Incident Details</Text>}>
        {detailTarget && (() => {
          let involvedNames: string[] = [];
          try {
            const parsed = JSON.parse(detailTarget.involvedPartyIds);
            if (Array.isArray(parsed) && parsed.length > 0) {
              involvedNames = parsed.map((x: string | {id: string; name: string}) =>
                typeof x === 'object' ? x.name : x
              );
            }
          } catch { /* ignore */ }
          return (
            <Stack gap="md">
              <Group>
                <Badge variant="light">{reportTypeLabel(detailTarget.reportType)}</Badge>
                <Badge color={SEVERITY_COLORS[detailTarget.severity]}>{detailTarget.severity}</Badge>
                {detailTarget.resolvedAt
                  ? <Badge color="var(--color-success)">Resolved</Badge>
                  : <Badge color="var(--color-error)" variant="light">Unresolved</Badge>
                }
              </Group>

              <Divider />

              <div>
                <Text size="xs" c="dimmed">Shelter</Text>
                <Text size="sm">{detailTarget.shelterId ? shelterName(detailTarget.shelterId) : '-'}</Text>
              </div>

              <div>
                <Text size="xs" c="dimmed">Description</Text>
                <Text size="sm">{detailTarget.description || '-'}</Text>
              </div>

              {involvedNames.length > 0 && (
                <div>
                  <Text size="xs" c="dimmed">Involved Parties</Text>
                  <Text size="sm">{involvedNames.join(', ')}</Text>
                </div>
              )}

              <div>
                <Text size="xs" c="dimmed">Responding Staff</Text>
                <Text size="sm">{detailTarget.respondingStaff || '-'}</Text>
              </div>

              {detailTarget.resolution && (
                <div>
                  <Text size="xs" c="dimmed">Resolution</Text>
                  <Text size="sm">{detailTarget.resolution}</Text>
                </div>
              )}

              <div>
                <Text size="xs" c="dimmed">Follow-up Needed</Text>
                <Badge size="sm" color={detailTarget.followUpNeeded ? '#C68700' : '#939598'}>
                  {detailTarget.followUpNeeded ? 'Yes' : 'No'}
                </Badge>
              </div>

              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <div>
                  <Text size="xs" c="dimmed">Reported At</Text>
                  <Text size="sm">{detailTarget.reportedAt ? new Date(detailTarget.reportedAt).toLocaleString() : '-'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Reported By</Text>
                  <Text size="sm">{detailTarget.reportedBy || '-'}</Text>
                </div>
              </SimpleGrid>

              {detailTarget.resolvedAt && (
                <div>
                  <Text size="xs" c="dimmed">Resolved At</Text>
                  <Text size="sm">{new Date(detailTarget.resolvedAt).toLocaleString()}</Text>
                </div>
              )}

              {detailTarget.notes && (
                <div>
                  <Text size="xs" c="dimmed">Notes</Text>
                  <Text size="sm">{detailTarget.notes}</Text>
                </div>
              )}

              <Divider />

              {canEdit && (
              <Group justify="flex-end">
                {!detailTarget.resolvedAt && (
                  <Button variant="light" color="var(--color-success)" leftSection={<IconCheck size={14} />}
                    onClick={() => { const t = detailTarget; setDetailTarget(null); handleResolve(t); }}>
                    Resolve
                  </Button>
                )}
                <Button variant="light" leftSection={<IconPencil size={14} />}
                  onClick={() => { const t = detailTarget; setDetailTarget(null); openEdit(t); }}>
                  Edit
                </Button>
              </Group>
              )}
            </Stack>
          );
        })()}
      </Drawer>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editTarget ? 'Edit Incident' : 'Report Incident'} size="lg">
        <Stack gap="sm">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Select label="Shelter" data={shelterSelectData} value={form.shelterId ?? ''}
              onChange={v => setField('shelterId', v ?? '')} required />
            <Select label="Report Type" data={INCIDENT_REPORT_TYPES.map(t => ({ value: t.value, label: t.label }))}
              value={form.reportType ?? 'other'} onChange={v => setField('reportType', v ?? 'other')} required />
          </SimpleGrid>
          <Select label="Severity" data={SEVERITY_OPTIONS}
            value={form.severity ?? 'low'} onChange={v => setField('severity', v ?? 'low')} required />
          <Textarea label="Description" rows={4} value={form.description ?? ''}
            onChange={e => setField('description', e.target.value)} required />
          {/* Involved Parties — People Directory search */}
          <div>
            <Text size="sm" fw={500} mb={4}>Involved Parties</Text>
            {involvedPeople.length > 0 && (
              <Group gap={6} mb={8} wrap="wrap">
                {involvedPeople.map(p => (
                  <Badge key={p.id} variant="light" color="var(--data-viz-650)" rightSection={
                    <ActionIcon size={12} variant="transparent" color="var(--data-viz-650)" onClick={() => removeInvolvedPerson(p.id)}>
                      <IconX size={10} />
                    </ActionIcon>
                  }>
                    {p.name}
                  </Badge>
                ))}
              </Group>
            )}
            <div style={{ position: 'relative' }}>
              <TextInput
                placeholder="Search People Directory by name…"
                leftSection={<IconSearch size={14} />}
                value={peopleSearch}
                onChange={e => handlePeopleSearch(e.target.value)}
                size="sm"
                rightSection={peopleSearching ? <span style={{ fontSize: 12, color: '#888' }}>…</span> : null}
              />
              {peopleResults.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  background: '#fff', border: '1px solid #e2e4e8', borderRadius: 4,
                  boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)', zIndex: 100, marginTop: 2,
                }}>
                  {peopleResults.map(p => (
                    <button key={p.id} onClick={() => addInvolvedPerson(p)}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14 }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                    >
                      <span style={{ fontWeight: 500 }}>{p.name}</span>
                      {p.phone && <span style={{ color: '#888', marginLeft: 8, fontSize: 12 }}>{formatPhone(p.phone)}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <TextInput label="Responding Staff" value={form.respondingStaff ?? ''}
            onChange={e => setField('respondingStaff', e.target.value)} />
          {editTarget && (
            <Textarea label="Resolution" rows={3} value={form.resolution ?? ''}
              onChange={e => setField('resolution', e.target.value)} />
          )}
          <Textarea label="Notes" rows={2} value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{editTarget ? 'Update' : 'Report'}</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
